import {
	formatCompactExpiryDatesForDisplay,
	formatExpiryDatesForDisplay,
} from '../../../common/utils/pickList/expiryDateHelpers';
import { getExpiryDatesBlockers, shouldShowExpiryBeforeNextServiceWarning } from '../../../common/utils/serviceDocket/helpers';
import { getLocalTime } from '../../../common/utils/time/time';

// Expiry dates line and warnings shown under a pick list row (pick lists only;
// confirm screens show their own expiry buttons).
export const getPlanogramItemExpiryInfo = ({
	shouldShowExpiryDates,
	isConfirmStockLevels,
	isConfirmValues,
	resolvedExpiryDates,
	expiryDatesQuantity,
	useSingleExpiryDateForMarket,
	shouldShowExpiryWarnings,
	tenantSettings,
	tenantTimezone,
	serviceRunPlanned,
	serviceFrequencyDays,
	product,
	expiryDatesDisplayLabel,
	isPickedWarehouseExpiryDates,
}) => {
	if (!shouldShowExpiryDates || isConfirmStockLevels || isConfirmValues) {
		return {
			expiryDatesDisplay: null,
			mandatoryWarningText: null,
			optionalWarningText: null,
			expiresBeforeNextServiceText: null,
			expiresBeforeNextServiceAdditionalText: null,
			expiresBeforeNextServiceDetailsText: null,
		};
	}

	if (!resolvedExpiryDates || !expiryDatesQuantity) {
		return {
			expiryDatesDisplay: null,
			mandatoryWarningText: null,
			optionalWarningText: null,
			expiresBeforeNextServiceText: null,
			expiresBeforeNextServiceAdditionalText: null,
			expiresBeforeNextServiceDetailsText: null,
		};
	}

	// Format dates for display
	const dates = formatExpiryDatesForDisplay(resolvedExpiryDates, expiryDatesQuantity, useSingleExpiryDateForMarket);
	const compactDates = formatCompactExpiryDatesForDisplay(dates, 2);
	if (!shouldShowExpiryWarnings) {
		return {
			expiryDatesDisplay: compactDates ? `${expiryDatesDisplayLabel}: ${compactDates}` : null,
			mandatoryWarningText: null,
			optionalWarningText: null,
			expiresBeforeNextServiceText: null,
			expiresBeforeNextServiceAdditionalText: null,
			expiresBeforeNextServiceDetailsText: null,
		};
	}

	// Get warnings using existing logic from confirm stock levels
	const expiryDatesArray = useSingleExpiryDateForMarket ? [resolvedExpiryDates?.[1]]
		: [...Array(expiryDatesQuantity)].map((_, i) => resolvedExpiryDates?.[i + 1] || null);

	const {
		expiredProductsCount,
		expireTodayCount,
		hasExpiryDatesNotFilledCount,
		productsExpiringWithinXDaysCount,
	} = getExpiryDatesBlockers(expiryDatesArray, tenantSettings, {
		referenceDate: serviceRunPlanned,
		timezone: tenantTimezone,
	});

	// Build warning text using same logic as ExpiryDateButtons
	const mandatoryWarnings = [];
	const optionalWarnings = [];

	if (expiredProductsCount) {
		mandatoryWarnings.push(`${expiredProductsCount} product(s) expired`);
	}
	if (expireTodayCount) {
		mandatoryWarnings.push(`${expireTodayCount} product(s) expire today`);
	}
	if (productsExpiringWithinXDaysCount) {
		optionalWarnings.push(`${productsExpiringWithinXDaysCount} product(s) expire within ${tenantSettings.expiryDateThresholds?.[0] || 7} days`);
	}
	if (hasExpiryDatesNotFilledCount) {
		if (isPickedWarehouseExpiryDates) {
			const productLabel = hasExpiryDatesNotFilledCount === 1 ? 'product' : 'products';
			optionalWarnings.push(`Unknown expiry for ${hasExpiryDatesNotFilledCount} picked ${productLabel}`);
		}
		else {
			optionalWarnings.push(`${hasExpiryDatesNotFilledCount} product(s) have unknown expiry`);
		}
	}

	const timezone = tenantTimezone || 'Australia/Melbourne';
	// Use averageDaysSinceLastService as a proxy for service frequency (rounded to whole days).
	const resolvedServiceFrequencyDays = Number.isFinite(serviceFrequencyDays) && serviceFrequencyDays > 0
		? Math.max(1, Math.round(serviceFrequencyDays))
		: 7;

	const currentDate = getLocalTime(timezone).startOf('day');
	const plannedDate = serviceRunPlanned ? getLocalTime(timezone, serviceRunPlanned).startOf('day') : null;
	const nextServiceDate = plannedDate ? plannedDate.clone().add(resolvedServiceFrequencyDays, 'days') : null;

	const expiringBeforeNextService = [];
	if (nextServiceDate && shouldShowExpiryBeforeNextServiceWarning({ tenantSettings, product })) {
		// Flag any filled expiry dates that fall before the estimated next service date.
		const totalItemsToCheck = useSingleExpiryDateForMarket ? 1 : expiryDatesQuantity;

		for (let i = 1; i <= totalItemsToCheck; i += 1) {
			const expiryDate = resolvedExpiryDates?.[i];
			if (!expiryDate) {
				continue;
			}

			const localExpiry = getLocalTime(timezone, expiryDate).startOf('day');
			if (!localExpiry.isAfter(currentDate)) {
				continue;
			}
			if (localExpiry.isBefore(nextServiceDate)) {
				expiringBeforeNextService.push(localExpiry);
			}
		}
	}

	let nextServiceWarningText = null;
	let nextServiceWarningAdditionalText = null;
	let nextServiceDetailsText = null;
	if (expiringBeforeNextService.length) {
		const sortedExpiringBeforeNextService = [...expiringBeforeNextService].sort((a, b) => a.diff(b));
		const [soonestExpiry] = sortedExpiringBeforeNextService;
		const daysUntilSoonestExpiry = soonestExpiry.diff(currentDate, 'days');

		const productLabel = 'product(s)';
		const verbLabel = expiringBeforeNextService.length === 1 ? 'expires' : 'expire';
		const daysLabel = daysUntilSoonestExpiry === 1 ? 'day' : 'days';
		nextServiceWarningText = `${expiringBeforeNextService.length} ${productLabel} ${verbLabel} before next service`;
		nextServiceWarningAdditionalText = `in ${daysUntilSoonestExpiry} ${daysLabel}`;

		const serviceFrequencyLabel = resolvedServiceFrequencyDays === 1 ? 'day' : 'days';
		const expiringBeforeNextServiceLabel = (() => {
			const preview = sortedExpiringBeforeNextService
				.slice(0, 4)
				.map(date => date.format('MMM DD'))
				.join(', ');

			const remaining = sortedExpiringBeforeNextService.length - 4;
			return remaining > 0 ? `${preview} and ${remaining} more` : preview;
		})();

		nextServiceDetailsText = [
			`Service frequency: ${resolvedServiceFrequencyDays} ${serviceFrequencyLabel}`,
			`Planned service: ${plannedDate.format('MMM DD')}`,
			`Next service: ${nextServiceDate.format('MMM DD')}`,
			`Soonest expiry: ${soonestExpiry.format('MMM DD')} (in ${daysUntilSoonestExpiry} ${daysLabel})`,
			`Expiring before next service: ${expiringBeforeNextServiceLabel}`,
		].join('\n');
	}

	return {
		expiryDatesDisplay: compactDates ? `${expiryDatesDisplayLabel}: ${compactDates}` : null,
		mandatoryWarningText: mandatoryWarnings.length > 0 ? mandatoryWarnings.join('\n') : null,
		optionalWarningText: optionalWarnings.length > 0 ? optionalWarnings.join('\n') : null,
		expiresBeforeNextServiceText: nextServiceWarningText,
		expiresBeforeNextServiceAdditionalText: nextServiceWarningAdditionalText,
		expiresBeforeNextServiceDetailsText: nextServiceDetailsText,
	};
};
